import { cn } from '@rocco/utils/cn';

/** Below this many views the compact figure keeps one decimal: "1.2K", not "1K". */
const SHORT_FIGURE_BELOW = 10_000;

/** One figure of the audience block, with what it counts under it. */
export interface AudienceStat {
    figure: string;
    label: string;
}

/**
 * What the profile has drawn, as figures rather than a sentence: "59K" over
 * "Views", "1:15" over "Time spent in min". Or as much of it as the profile
 * has — a zero on either side drops that stat rather than printing it.
 *
 * The views are compacted the way a reader says them: a profile with 59,373
 * of them has "59K", one with 1,234 has "1.2K" — the decimal is worth having
 * while the figure is small — and under a thousand the number stands as it
 * is.
 *
 * `TotalTimeSpent` is the sum of every visit in seconds, which on an
 * established profile is four million of them — "1225:51 hrs" is true and
 * tells a reader nothing. Divided by the views it becomes how long the average
 * visitor stayed, which is the figure worth showing.
 */
export const getAudience = (
    kpi?: {
        TotalPageViews?: number | null;
        TotalTimeSpent?: number | null;
    } | null,
): AudienceStat[] => {
    if (!kpi) return [];
    const views = kpi.TotalPageViews ?? 0;
    const stats: AudienceStat[] = [];
    if (views) {
        stats.push({
            figure: new Intl.NumberFormat('en-NZ', {
                notation: 'compact',
                maximumFractionDigits: views < SHORT_FIGURE_BELOW ? 1 : 0,
            }).format(views),
            label: 'Views',
        });
    }
    if (kpi.TotalTimeSpent && views) {
        const seconds = Math.round(kpi.TotalTimeSpent / views);
        const minutes = Math.floor(seconds / 60);
        stats.push({
            figure: `${minutes}:${String(seconds % 60).padStart(2, '0')}`,
            label: 'Time spent in min',
        });
    }
    return stats;
};

/**
 * The audience under the discipline: each figure large and light, its label
 * small beneath, a hairline between the two. Sized against the name above it
 * — the figures are a third of its height, so they read as a footnote to it
 * rather than a second heading.
 */
const AudienceBlock = ({ stats }: { stats: AudienceStat[] }) => (
    <div className="flex items-stretch justify-center max-md:mt-3 md:mt-[1.5rem]">
        {stats.map((stat, index) => (
            <div key={stat.label} className="flex items-stretch">
                {index > 0 ? (
                    <span
                        aria-hidden="true"
                        className="w-px self-stretch bg-white/50 max-md:mx-[1.25rem] md:mx-[1.75rem]"
                    />
                ) : null}
                <div className="flex flex-col items-center">
                    <span className="font-sans leading-none font-light tracking-[-0.02em] text-white max-md:text-[1.75rem] md:text-[2.5rem]">
                        {stat.figure}
                    </span>
                    <span className="mt-[0.375rem] font-sans text-[0.75rem] leading-none font-normal whitespace-nowrap text-white/70">
                        {stat.label}
                    </span>
                </div>
            </div>
        ))}
    </div>
);

/** The discipline's reveal, and the audience's a beat behind it. */
const REVEAL_MS = 600;
const AUDIENCE_DELAY_MS = 150;
const REVEAL_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const revealStyle = (revealed: boolean | undefined, delay: number) =>
    revealed === undefined
        ? undefined
        : {
              transitionDuration: `${REVEAL_MS}ms`,
              transitionDelay: `${delay}ms`,
              transitionTimingFunction: REVEAL_EASING,
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateY(0)' : 'translateY(0.75rem)',
          };

const REVEAL_CLASS =
    'transition-[opacity,transform] ease-out motion-reduce:transition-none';

/**
 * What sits under the name: the discipline as a rule of type, and the
 * audience figures under that.
 *
 * One component for the banner and for the intro that plays over it, so the
 * two groups are the same height to the pixel. The intro fades out over the
 * banner with the name centred in both, and a line present in one and not the
 * other moves that centre — the name jumped as the intro ended, and whatever
 * the banner alone carried appeared in the same instant.
 *
 * `revealed` is the intro's: undefined leaves the caption static, as the
 * banner wants it; a boolean plays the reveal, the discipline first and the
 * figures a beat behind, both landed before the intro hands over.
 */
export const ProfessionalHeroCaption = ({
    discipline,
    audience,
    revealed,
}: {
    discipline?: string;
    audience: AudienceStat[];
    revealed?: boolean;
}) => {
    if (!discipline && audience.length === 0) return null;
    return (
        <>
            {discipline ? (
                // Set wide enough that it reads as a rule of type under the
                // name rather than as a second line of it — the name is
                // 120px and tight, so the only way this sits under it
                // without competing is to be small and open.
                <p
                    className={cn(
                        'm-0 font-sans font-normal text-white uppercase max-md:mt-3 max-md:text-[0.875rem] max-md:tracking-[0.35em] md:mt-[1.5rem] md:text-[1rem] md:tracking-[0.55em]',
                        // The banner centres its whole block; the intro's
                        // group takes the width of the name, so this line
                        // has to centre itself or it sits at the left of
                        // the name and jumps to the middle at the handover.
                        revealed !== undefined &&
                            cn(REVEAL_CLASS, 'text-center'),
                    )}
                    style={revealStyle(revealed, 0)}
                >
                    {discipline}
                </p>
            ) : null}
            {audience.length > 0 ? (
                <div
                    className={cn(revealed !== undefined && REVEAL_CLASS)}
                    style={revealStyle(revealed, AUDIENCE_DELAY_MS)}
                >
                    <AudienceBlock stats={audience} />
                </div>
            ) : null}
        </>
    );
};
